import {
  CalendarTask,
  CommonUtils,
  IEntity,
  StandardEntity,
} from '@infodata-group/inf-commons';
import { AbsenceType } from './absence-type';
import { AbsenceTypeGroup } from './absence-type-group';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { AbsenceInputTypeEnum } from '../enum/absence-input-type.enum';

dayjs.extend(isBetween);

export class Absence extends CalendarTask implements IEntity {
  get start(): Date {
    return this.dateStart.toDate();
  }

  get end(): Date {
    return this.dateEnd.toDate();
  }

  get fullDay(): boolean {
    return (
      this.inputType === AbsenceInputTypeEnum.full ||
      this.inputType === undefined
    );
  }

  get partialDay(): boolean {
    return this.inputType === AbsenceInputTypeEnum.partial;
  }

  get detailsDay(): boolean {
    return this.inputType === AbsenceInputTypeEnum.details;
  }

  get dateStartFormatted(): string {
    return CommonUtils.convertDateToString(this.dateStart.toDate());
  }

  get dateEndFormatted(): string {
    return CommonUtils.convertDateToString(this.dateEnd.toDate());
  }

  get state(): StandardEntity {
    return this._state;
  }

  set state(value: StandardEntity) {
    this._state = value;
    switch (value.id) {
      case '0':
        this.backgroundColor = this.backgroundColorBase + '33';
        this.color = this.backgroundColorBase;
        break;
      case '1':
        this.backgroundColor = this.backgroundColorBase;
        this.color = '#2F373A';
        break;
      case 'F':
        this.backgroundColor = '#C8C8C8';
        this.color = '#2F373A';
        break;
      default:
        this.backgroundColor = '#FAFAFA';
        this.color = '#969696';
    }
    this.stateId = value.id;
  }

  get stateColor(): string {
    switch (this.stateId) {
      case '0':
        return '#C8C8C8';
      case '1':
        return '#48cfad';
      case '2':
        return '#ED5565';
      case '3':
        return '#FAAA0A';
    }
  }

  get iconColor(): string {
    return this.absenceCode.color;
  }

  constructor(id: string, type: AbsenceType, dateStart: Date, dateEnd: Date) {
    super({
      id: id,
      label: '',
      dateStart: dateStart,
      dateEnd: dateEnd,
    });
    if (type && type.getId() === 'F') {
      this.label = type.getLabel();
    }
    if (type) {
      this.absenceTypeGroup = type.group;
      this.absenceCode = type;
      this.borderColor = type.color;
      this.backgroundColorBase = type.color;
      this.backgroundColor = type.color;
    }
    this.supervisors = [];
  }

  public static WAITING = '0';
  public static VALIDATE = '1';
  public static DISPROVE = '2';
  public static CANCEL = '3';

  public absenceCode: AbsenceType;
  public justification: string;
  public userComment: string[];
  public message: string[];
  public total: string;
  public totalCentieme: number;
  public service: StandardEntity;
  public pay: string;
  private _state: StandardEntity;
  public stateId: string;
  public timeStart: string;
  public timeEnd: string;
  public supervisors: StandardEntity[];
  public icon = 'square icon-bold';
  public user: StandardEntity;
  public userId: string;
  public userLabel: string;
  public backgroundColorBase: string;
  public isHoliday: boolean;
  public timeSlot: StandardEntity;
  public absenceTypeCode: string;
  public absenceType: StandardEntity;
  public absenceDays: Date[] = [];
  public daysDuration: string[] = [];
  public informant: boolean;

  public inputType = AbsenceInputTypeEnum.full;

  // public absenceType: AbsenceType;
  public absenceTypeGroup: AbsenceTypeGroup;

  public lock = false;

  private static sanitize(serial: any) {
    const data: any = {};
    for (const attr in serial) {
      if (serial[attr] !== null && serial[attr] !== undefined) {
        data[attr] = serial[attr];
      }
    }
    return data;
  }

  getId(): string {
    return this.id;
  }

  getLabel(): string {
    return this.label;
  }

  public serialize(): any {
    const dateEnd = !this.partialDay
      ? CommonUtils.convertDateToString(this.dateEnd.toDate())
      : CommonUtils.convertDateToString(this.dateStart.toDate());
    const serial = {
      id: this.id,
      absenceCode: this.absenceCode.id,
      dateStart: CommonUtils.convertDateToString(this.dateStart.toDate()),
      timeStart: this.detailsDay ? this.timeStart : null,
      dateEnd: dateEnd,
      timeEnd: this.detailsDay ? this.timeEnd : null,
      category:
        this.absenceCode && this.absenceCode.group
          ? this.absenceCode.group.id
          : null,
      userComment: this.userComment ?? null,
      justification: this.justification ?? null,
      supervisor: this.supervisors ?? null,
      inputType: this.inputType,
      state: this.stateId ?? '0',
      personneCode: this.user?.id ?? null,
      timeSlot: this.partialDay ? this.timeSlot.id : null,
    };
    return Absence.sanitize(serial);
  }

  clone() {
    const absence = new Absence(
      this.id,
      this.absenceCode,
      this.dateStart.toDate(),
      this.dateEnd.toDate(),
    );
    absence.absenceCode = this.absenceCode;
    absence.timeStart = this.timeStart;
    absence.timeEnd = this.timeEnd;
    absence.userComment = this.userComment;
    absence.justification = this.justification;
    absence.total = this.total;
    absence.pay = this.pay;
    if (this.state) {
      absence.state = this._state;
    }
    absence.supervisors = this.supervisors?.map(
      (s) => new StandardEntity(s.id, s.label),
    );
    absence.user = this.user;
    return absence;
  }

  setDate(start: Date) {
    this.dateStart = dayjs(start);
    this.dateEnd = dayjs(start).add(1, 'h');
  }

  public getDateOnYear(year: number): Date[] {
    let ref = dayjs(new Date(year, 0, 1));
    const start = dayjs(this.dateStart);
    const end = dayjs(this.dateEnd);
    const dates = [];
    if (start.isAfter(ref, 'd') && start.year() === year) {
      ref = dayjs(start);
    }
    while (ref.isBetween(start, end, 'd', '[]')) {
      dates.push(ref.toDate());
      ref = ref.add(1, 'd');
    }
    return dates;
  }

  public isOneDay(): boolean {
    return this.dateStart.isSame(this.dateEnd, 'day');
  }

  dayDurationOnMonth(month: number, year: number) {
    const monthStart = dayjs(new Date(year, month, 1));
    const monthEnd = monthStart.clone().endOf('month');
    const end = monthEnd.isBefore(this.dateEnd.toDate())
      ? monthEnd.date()
      : this.dateEnd.date();
    const start = monthStart.isAfter(this.dateStart.toDate())
      ? monthStart.date()
      : this.dateStart.date();

    return end - start + 1;
  }

  isValid(): boolean {
    if (this.partialDay) {
      return true;
    }
    const start = dayjs(this.dateStart);
    const end = dayjs(this.dateEnd);
    const sd = start.toDate();
    const ed = end.toDate();

    switch (this.inputType) {
      case AbsenceInputTypeEnum.full:
        return start.isBefore(end, 'd') || start.isSame(end, 'd');
      case AbsenceInputTypeEnum.details:
        CommonUtils.addTimeToDate(sd, this.timeStart);
        CommonUtils.addTimeToDate(ed, this.timeEnd);
        return dayjs(sd).isBefore(dayjs(ed));
    }
  }
}
